// sampleData.js (P4-Onboarding) — one-click demo/sample data for a fresh tenant.
//
// Seeds a realistic Irish food-&-beverage-wholesaler dataset so EVERY major screen looks
// alive (Products/Units, Price Lists, Customer Groups, Suppliers/POs/GRN/Inventory,
// Quotations→Sales Orders→Delivery Notes→Invoices→Payments, a Credit Note, Expenses,
// Statements, VAT, Customer Intelligence). It orchestrates ONLY public data-layer
// functions — never raw Supabase and never a protected write path — so tenancy/RLS and
// all business rules are enforced by those layers. The manifest of created ids is stored
// on business_settings.demo_manifest and built incrementally, so a mid-run failure is
// still cleanable via clearDemoData().

import { createProduct, deleteProduct, createCategory, deleteCategory, getProducts } from "./products";
import { createUnit } from "./productUnits";
import { createCustomer, deleteCustomer, listCustomers } from "./customers";
import { createSupplier, deleteSupplier } from "./suppliers";
import { createPurchaseOrder, deletePurchaseOrder } from "./purchaseOrders";
import { createGRN, deleteGRN } from "./goodsReceived";
import { createPriceList, addItem, setDefaultPriceList, archivePriceList, removeItem } from "./priceLists";
import { createCustomerGroup, deleteCustomerGroup, assignCustomerToGroup, setCustomerPriceList } from "./customerGroups";
import { addCustomerPrice, removeCustomerPrice } from "./customerPrices";
import { createQuotation, convertToSalesOrder, deleteQuotation } from "./quotations";
import { createSalesOrder, deleteSalesOrder, getSalesOrder } from "./salesOrders";
import { createDeliveryNote, deleteDeliveryNote } from "./deliveryNotes";
import { createInvoice, createFromSalesOrder, markSent, cancelInvoice, deleteInvoice, getInvoice } from "./invoices";
import { recordPayment, voidPayment, listPayments } from "./payments";
import { createCreditNote, issueCreditNote, cancelDraft, CREDIT_REASONS } from "./creditNotes";
import { createExpense, deleteExpense } from "./expenses";
import { resolvePrices } from "./pricing";
import { getDemoManifest, setDemoManifest } from "./businessSettings";
import { todayISO, addDaysISO } from "./dates";
import { demoDataAllowed, DEMO_BLOCKED_MESSAGE } from "./demoEnvironment";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Calendar arithmetic (todayISO -> addDaysISO), not Date.now() - n*86400000 — millisecond
// arithmetic drifts by an hour across any DST transition inside the window, which can
// land a "N days ago" figure on the wrong calendar day right at the boundary.
const daysAgo = (n) => addDaysISO(todayISO(), -n);
const count = (res) => (Array.isArray(res) ? res.length : (res?.count ?? res?.data?.length ?? 0));

// A fresh, empty manifest (every id we create, in dependency order).
const emptyManifest = () => ({
  version: 1, seededAt: null,
  categories: [], products: [], units: [], suppliers: [], purchaseOrders: [], grns: [],
  priceLists: [], priceListItems: [], customerGroups: [], customers: [], customerPrices: [],
  quotations: [], salesOrders: [], deliveryNotes: [], invoices: [], payments: [], creditNotes: [], expenses: [],
});

// ── Status ──────────────────────────────────────────────────────────────────
/** { workspaceEmpty, seeded } — workspaceEmpty = no products AND no customers. */
export async function demoStatus() {
  const [prods, custs, manifest] = await Promise.all([
    getProducts({ pageSize: 1 }).catch(() => ({ count: 0 })),
    listCustomers({ pageSize: 1 }).catch(() => ({ count: 0 })),
    getDemoManifest().catch(() => null),
  ]);
  return { workspaceEmpty: count(prods) === 0 && count(custs) === 0, seeded: !!manifest };
}

// ── The catalogue (EUR wholesale prices; Irish VAT bands; DRS where applicable) ──
const CATALOGUE = [
  { key: "spark",   name: "Sparkling Water 330ml",   sku: "BEV-SPK-330", price: 0.60, cost: 0.32, vat: 23, drs: 0.15, case: 24 },
  { key: "still",   name: "Still Water 500ml",       sku: "BEV-STL-500", price: 0.55, cost: 0.28, vat: 23, drs: 0.15, case: 24 },
  { key: "cola",    name: "Cola 330ml Can",          sku: "BEV-COL-330", price: 0.70, cost: 0.38, vat: 23, drs: 0.15, case: 24 },
  { key: "oj",      name: "Orange Juice 1L",         sku: "BEV-OJ-1L",   price: 1.80, cost: 1.05, vat: 23, drs: 0,    case: 12 },
  { key: "coffee",  name: "Ground Coffee 250g",      sku: "HOT-COF-250", price: 4.50, cost: 2.80, vat: 23, drs: 0,    case: 6  },
  { key: "ketchup", name: "Tomato Ketchup 500ml",    sku: "AMB-KET-500", price: 1.60, cost: 0.90, vat: 23, drs: 0,    case: 12 },
  { key: "oil",     name: "Catering Olive Oil 5L",   sku: "AMB-OIL-5L",  price: 22.00, cost: 15.50, vat: 23, drs: 0,   case: 0  },
  { key: "bread",   name: "Brown Soda Bread (loaf)", sku: "BAK-BRD-01",  price: 1.20, cost: 0.60, vat: 0,  drs: 0,    case: 0  },
  { key: "tea",     name: "Breakfast Tea 80 Bags",   sku: "HOT-TEA-80",  price: 2.20, cost: 1.20, vat: 0,  drs: 0,    case: 0  },
  { key: "cheddar", name: "Irish Cheddar 1kg",       sku: "DAI-CHE-1K",  price: 9.50, cost: 6.20, vat: 0,  drs: 0,    case: 0  },
];

const CUSTOMERS = [
  { key: "harbour",   name: "The Harbour Café",       county: "Dublin",   vat: "IE0000001W", city: "Howth",     line1: "12 West Pier",        group: "hosp" },
  { key: "supervalu", name: "O'Brien's SuperValu",    county: "Cork",     vat: "IE0000002H", city: "Midleton",  line1: "Main Street",         group: "retail" },
  { key: "galway",    name: "Galway Bay Hotel",       county: "Galway",   vat: "IE0000003K", city: "Salthill",  line1: "The Promenade",       group: "hosp", cprice: { key: "coffee", price: 3.90 } },
  { key: "deli",      name: "Dublin Deli Co.",        county: "Dublin",   vat: "IE0000004L", city: "Dublin 2",  line1: "8 Dawson Street" },
  { key: "catering",  name: "Cork Catering Ltd",      county: "Cork",     vat: "IE0000005M", city: "Cork",      line1: "Unit 4, Kinsale Road", ownList: "hosp" },
  { key: "corner",    name: "Kelly's Corner Shop",    county: "Kilkenny", vat: "",           city: "Kilkenny",  line1: "3 Market Square",     group: "retail" },
  { key: "mill",      name: "The Old Mill Restaurant", county: "Clare",   vat: "IE0000007P", city: "Ennis",     line1: "Mill Road",           group: "hosp" },
  { key: "shannon",   name: "Shannon Foodservice",    county: "Limerick", vat: "IE0000008Q", city: "Shannon",   line1: "Smithstown Ind. Est.", ownList: "retail" },
];

// ── Load ────────────────────────────────────────────────────────────────────
/**
 * Seed the full demo dataset. Gated: refuses unless the workspace is empty AND not
 * already seeded. Persists the manifest incrementally. `onProgress(label)` per step.
 */
export async function loadDemoData({ onProgress } = {}) {
  if (!demoDataAllowed()) {
    const e = new Error(DEMO_BLOCKED_MESSAGE);
    e.code = "ENV_NOT_ALLOWED";
    throw e;
  }
  const status = await demoStatus();
  if (status.seeded) { const e = new Error("Demo data is already loaded. Clear it first to reload."); e.code = "ALREADY_SEEDED"; throw e; }
  if (!status.workspaceEmpty) { const e = new Error("Demo data can only be loaded into an empty workspace (no products and no customers)."); e.code = "NOT_EMPTY"; throw e; }

  const m = emptyManifest();
  m.seededAt = new Date().toISOString();
  const step = (label) => { onProgress && onProgress(label); };
  const save = () => setDemoManifest(m);
  await save();   // stake the marker immediately so a mid-run failure is cleanable

  // 1) Products (+ pack units)
  step("Adding products & pack units…");
  const cat = await createCategory({ name: "Wholesale Stock" });
  m.categories.push(cat.id);
  const P = {};
  for (const c of CATALOGUE) {
    const prod = await createProduct({
      name: c.name, sku: c.sku, category_id: cat.id, unit: "each",
      wholesale_price: c.price, cost_price: c.cost, vat_rate: c.vat,
      drs_applicable: c.drs > 0, drs_rate: c.drs, status: "active", in_stock: true,
      description: `${c.name} — wholesale`,
    });
    P[c.key] = { ...prod, caseUnitId: null };
    m.products.push(prod.id);
    if (c.case > 0) {
      const eachId = await createUnit(prod.id, { code: "each", name: "Each", factor: 1 });   // base
      const caseId = await createUnit(prod.id, { code: "case", name: `Case of ${c.case}`, factor: c.case });
      P[c.key].caseUnitId = caseId; P[c.key].caseSize = c.case;
      m.units.push(eachId, caseId);
    }
  }
  await save();

  // 2) Stock in — suppliers → purchase orders → goods received (legitimate arrival)
  step("Receiving stock (suppliers, purchase orders, GRNs)…");
  const sup1 = await createSupplier({ company_name: "Emerald Beverages Ltd", contact_name: "Declan Fahey", email: "orders@emeraldbev.ie", county: "Dublin", vat_number: "IE4471200A", payment_terms: "net_30", status: "active" });
  const sup2 = await createSupplier({ company_name: "Atlantic Foods Wholesale", contact_name: "Máire Nolan", email: "sales@atlanticfoods.ie", county: "Galway", vat_number: "IE5582311B", payment_terms: "net_30", status: "active" });
  m.suppliers.push(sup1.id, sup2.id);
  const receipts = [
    { sup: sup1.id, ref: "EB-4471", items: [["spark", 480], ["still", 480], ["cola", 240], ["oj", 120], ["coffee", 60]] },
    { sup: sup2.id, ref: "AF-2231", items: [["ketchup", 120], ["oil", 40], ["bread", 200], ["tea", 90], ["cheddar", 60]] },
  ];
  for (const r of receipts) {
    const poLines = r.items.map(([k, qty]) => ({ product_id: P[k].id, sku: P[k].sku, description: P[k].name, quantity: qty, unit_cost: CATALOGUE.find((c) => c.key === k).cost, vat_rate: P[k].vat_rate }));
    const poId = await createPurchaseOrder({ supplier_id: r.sup, order_date: daysAgo(40), status: "sent", payment_terms: "net_30", lines: poLines });
    m.purchaseOrders.push(poId);
    const grnLines = r.items.map(([k, qty]) => ({ product_id: P[k].id, sku: P[k].sku, description: P[k].name, ordered_quantity: qty, quantity_received: qty, unit_cost: CATALOGUE.find((c) => c.key === k).cost }));
    const grnId = await createGRN({ purchase_order_id: poId, supplier_id: r.sup, delivery_date: daysAgo(38), delivery_reference: r.ref, status: "fully_received", lines: grnLines });
    m.grns.push(grnId);
  }
  await save();

  // 3) Price lists (+ per-case items) and customer groups
  step("Building price lists & customer groups…");
  const listHosp = await createPriceList({ name: "Wholesale — Hospitality", currency: "EUR" });
  const listRetail = await createPriceList({ name: "Wholesale — Retail", currency: "EUR" });
  m.priceLists.push(listHosp.id, listRetail.id);
  await setDefaultPriceList(listRetail.id);
  const caseItems = { // per-case list prices (a discount vs unit × case size)
    hosp:   { spark: 12.00, still: 11.00, cola: 15.00, oj: 19.00, coffee: 24.00 },
    retail: { spark: 12.60, still: 11.60, cola: 15.60, oj: 19.80, coffee: 25.20 },
  };
  for (const [list, prices] of [[listHosp.id, caseItems.hosp], [listRetail.id, caseItems.retail]]) {
    for (const [k, price] of Object.entries(prices)) {
      const it = await addItem(list, { product_id: P[k].id, unit_id: P[k].caseUnitId, min_qty: 1, unit_price: price });
      m.priceListItems.push(it.id);
    }
  }
  // a quantity break on coffee (per-each) in the hospitality list
  const brk = await addItem(listHosp.id, { product_id: P.coffee.id, unit_id: null, min_qty: 24, unit_price: 4.10 });
  m.priceListItems.push(brk.id);
  const grpHosp = await createCustomerGroup({ name: "Hospitality", price_list_id: listHosp.id, notes: "Cafés, hotels & restaurants" });
  const grpRetail = await createCustomerGroup({ name: "Retail", price_list_id: listRetail.id, notes: "Shops & forecourts" });
  m.customerGroups.push(grpHosp.id, grpRetail.id);
  await save();

  // 4) Customers (+ group / own-list / negotiated-price assignments — all resolver tiers)
  step("Adding customers & pricing assignments…");
  const C = {};
  for (const cu of CUSTOMERS) {
    const id = await createCustomer({
      company_name: cu.name, customer_type: "business", status: "active", vat_number: cu.vat || "",
      preferred_currency: "EUR", payment_terms: "net_30",
      addresses: [{ address_type: "delivery", line1: cu.line1, city: cu.city, county: cu.county, country: "Ireland", is_default: true }],
    });
    C[cu.key] = id; m.customers.push(id);
    if (cu.group === "hosp") await assignCustomerToGroup(id, grpHosp.id);
    if (cu.group === "retail") await assignCustomerToGroup(id, grpRetail.id);
    if (cu.ownList === "hosp") await setCustomerPriceList(id, listHosp.id);
    if (cu.ownList === "retail") await setCustomerPriceList(id, listRetail.id);
    if (cu.cprice) { const cp = await addCustomerPrice(id, { product_id: P[cu.cprice.key].id, unit_id: null, min_qty: 1, unit_price: cu.cprice.price }); m.customerPrices.push(cp.id); }
  }
  await save();

  // helper — price a set of {product, qty} lines through the resolver for a customer
  const buildLines = async (customerId, specs) => {
    const priced = await resolvePrices(customerId, specs.map((s) => ({ productId: s.p.id, qty: s.qty })));
    return specs.map((s, i) => ({ product_id: s.p.id, sku: s.p.sku, description: s.p.name, quantity: s.qty, unit_price: priced[i].unitPrice, discount_percent: 0, vat_rate: s.p.vat_rate }));
  };

  // 5) Quotations (2 converted to sales orders)
  step("Creating quotations & sales orders…");
  const quoteSpecs = [
    { cust: "harbour",  when: 70, specs: [{ p: P.spark, qty: 5 }, { p: P.coffee, qty: 30 }], convert: true },
    { cust: "galway",   when: 62, specs: [{ p: P.coffee, qty: 40 }, { p: P.oj, qty: 6 }],     convert: true },
    { cust: "deli",     when: 40, specs: [{ p: P.cheddar, qty: 12 }, { p: P.bread, qty: 30 }] },
    { cust: "mill",     when: 20, specs: [{ p: P.oil, qty: 8 }, { p: P.ketchup, qty: 24 }] },
    { cust: "supervalu", when: 8, specs: [{ p: P.cola, qty: 10 }, { p: P.tea, qty: 20 }] },
  ];
  const soIds = [];
  for (const q of quoteSpecs) {
    const lines = await buildLines(C[q.cust], q.specs);
    const qId = await createQuotation({ customer_id: C[q.cust], customer_name: CUSTOMERS.find((c) => c.key === q.cust).name, issue_date: daysAgo(q.when), expiry_date: daysAgo(q.when - 30), status: "sent", preferred_currency: "EUR", lines });
    m.quotations.push(qId);
    if (q.convert) { const { salesOrderId } = await convertToSalesOrder(qId); m.salesOrders.push(salesOrderId); soIds.push({ id: salesOrderId, cust: q.cust }); }
  }
  // direct sales orders
  const directSO = [
    { cust: "catering", when: 30, specs: [{ p: P.oil, qty: 12 }, { p: P.ketchup, qty: 24 }, { p: P.coffee, qty: 18 }] },
    { cust: "shannon",  when: 18, specs: [{ p: P.still, qty: 4 }, { p: P.cola, qty: 6 }] },
    { cust: "corner",   when: 6,  specs: [{ p: P.tea, qty: 15 }, { p: P.bread, qty: 20 }] },
  ];
  for (const so of directSO) {
    const lines = await buildLines(C[so.cust], so.specs);
    const soId = await createSalesOrder({ customer_id: C[so.cust], customer_name: CUSTOMERS.find((c) => c.key === so.cust).name, order_date: daysAgo(so.when), status: "confirmed", preferred_currency: "EUR", lines });
    m.salesOrders.push(soId); soIds.push({ id: soId, cust: so.cust });
  }
  await save();

  // 6) Delivery notes (dispatch 3 of the sales orders)
  step("Dispatching delivery notes…");
  for (const { id: soId } of soIds.slice(0, 3)) {
    const so = await getSalesOrder(soId);
    const dnLines = (so.sales_order_lines || []).map((l) => ({
      sales_order_line_id: l.id, product_id: l.product_id, sku: l.sku, description: l.description,
      ordered_quantity: l.quantity, reserved_quantity: l.reserved_quantity,
      dispatched_quantity: l.reserved_quantity || l.quantity, unit_price: l.unit_price,
    }));
    const dnId = await createDeliveryNote({ customer_id: so.customer_id, customer_name: so.customer_name, sales_order_id: soId, sales_order_number: so.so_number, delivery_date: daysAgo(3), status: "delivered", driver: "Padraig", lines: dnLines });
    m.deliveryNotes.push(dnId);
  }
  await save();

  // 7) Invoices — mixed paid / partial / overdue / sent (dates spread for the charts)
  step("Issuing invoices & recording payments…");
  const invPlan = [
    { cust: "harbour",  issue: 75, terms: 30, specs: [{ p: P.spark, qty: 5 }, { p: P.coffee, qty: 30 }], pay: "full" },
    { cust: "galway",   issue: 60, terms: 30, specs: [{ p: P.coffee, qty: 40 }, { p: P.oj, qty: 6 }],     pay: "partial" },
    { cust: "supervalu", issue: 50, terms: 30, specs: [{ p: P.cola, qty: 10 }, { p: P.tea, qty: 20 }],    pay: "none" },   // overdue
    { cust: "mill",     issue: 40, terms: 30, specs: [{ p: P.oil, qty: 8 }, { p: P.ketchup, qty: 24 }],   pay: "none" },   // overdue
    { cust: "deli",     issue: 12, terms: 30, specs: [{ p: P.cheddar, qty: 12 }, { p: P.bread, qty: 30 }], pay: "full" },
  ];
  const dueFrom = (issueDays, terms) => daysAgo(issueDays - terms);
  let paidHarbourInvId = null;
  for (const inv of invPlan) {
    const name = CUSTOMERS.find((c) => c.key === inv.cust).name;
    const lines = (await buildLines(C[inv.cust], inv.specs)).map((l) => {
      const cat = CATALOGUE.find((c) => c.sku === l.sku);
      return { ...l, drs: cat.drs > 0, drs_amount: cat.drs };
    });
    const invId = await createInvoice({ customer_id: C[inv.cust], customer_name: name, issue_date: daysAgo(inv.issue), due_date: dueFrom(inv.issue, inv.terms), payment_terms: "net_30", preferred_currency: "EUR", status: "draft", lines });
    m.invoices.push(invId);
    await markSent(invId);
    if (inv.pay !== "none") {
      const full = await getInvoice(invId);
      const total = Number(full.total_amount) || 0;
      const amount = inv.pay === "full" ? total : round2(total * 0.5);
      const pay = await recordPayment(invId, { amount, payment_date: daysAgo(inv.issue - 10) });
      if (pay?.id) m.payments.push(pay.id);
      if (inv.cust === "harbour") paidHarbourInvId = invId;
    }
  }
  // one invoice generated from a sales order (shows the SO→invoice chain), then sent
  const chainInvId = await createFromSalesOrder(soIds[soIds.length - 1].id);
  m.invoices.push(chainInvId); await markSent(chainInvId);
  await save();

  // 8) A credit note against a paid invoice (partial return)
  step("Issuing a credit note…");
  if (paidHarbourInvId) {
    const inv = await getInvoice(paidHarbourInvId);
    const line = (inv.invoice_lines || [])[0];
    if (line) {
      const cnId = await createCreditNote({
        invoice_id: paidHarbourInvId, customer_id: inv.customer_id, customer_name: inv.customer_name,
        issue_date: daysAgo(20), reason: CREDIT_REASONS[0]?.value || "damaged", notes: "Two units returned damaged in transit.",
        lines: [{ invoice_line_id: line.id, product_id: line.product_id, sku: line.sku, description: line.description, quantity: 2, unit_price: line.unit_price, discount_percent: 0, vat_rate: line.vat_rate }],
      });
      m.creditNotes.push(cnId);
      await issueCreditNote(cnId);
    }
  }
  await save();

  // 9) Expenses (varied categories & dates)
  step("Logging expenses…");
  const expenses = [
    { date: 60, supplier: "Glenmore Insurance", desc: "Van & premises insurance", category: "insurance", vat: 0,  total: 450.00 },
    { date: 45, supplier: "Coldstream Refrigeration", desc: "Walk-in fridge repair",     category: "repairs",   vat: 23, total: 560.00 },
    { date: 30, supplier: "Forecourt Fuels",     desc: "Diesel — delivery van",     category: "fuel",      vat: 23, total: 82.50 },
    { date: 20, supplier: "Northgate Property", desc: "Warehouse rent (month)",   category: "rent",      vat: 0,  total: 1200.00 },
    { date: 12, supplier: "Midland Power", desc: "Electricity",           category: "utilities", vat: 13.5, total: 340.00 },
  ];
  for (const e of expenses) {
    const exp = await createExpense({ expense_date: daysAgo(e.date), supplier: e.supplier, description: e.desc, category: e.category, vat_rate: e.vat, vat_reclaimable: true, total_amount: e.total });
    m.expenses.push(exp.id);   // createExpense returns the row object — store its id
  }
  await save();

  step("Done.");
  return m;
}

// ── Clear ───────────────────────────────────────────────────────────────────
/**
 * Best-effort reverse-dependency teardown of the seeded demo data. Voids payments,
 * cancels issued invoices, and deletes everything deletable; immutable residue
 * (cancelled invoices, issued credit notes, archived price lists) is reported honestly.
 */
export async function clearDemoData() {
  const m = await getDemoManifest();
  if (!m) { const e = new Error("No demo data to clear."); e.code = "NOT_SEEDED"; throw e; }
  const residue = [];
  const tryEach = async (ids, fn, label, pinnedReason) => {
    for (const id of ids || []) { try { await fn(id); } catch (e) { residue.push(pinnedReason ? `${label} ${id}: ${pinnedReason}` : `${label}: ${e.message}`); } }
  };

  // payments (void via each invoice's payment list — robust) then credit notes → invoices
  for (const invId of m.invoices || []) {
    try { for (const p of await listPayments(invId)) { try { await voidPayment(p.id); } catch { /* already void */ } } } catch { /* gone */ }
  }
  await tryEach(m.creditNotes, async (id) => { try { await cancelDraft(id); } catch { residue.push(`credit note ${id}: issued — retained as history`); } }, "credit note");
  await tryEach(m.invoices, async (id) => { try { await deleteInvoice(id); } catch { await cancelInvoice(id); residue.push(`invoice ${id}: issued — cancelled, retained as history`); } }, "invoice");
  await tryEach(m.deliveryNotes, deleteDeliveryNote, "delivery note");
  await tryEach(m.salesOrders, deleteSalesOrder, "sales order");
  await tryEach(m.quotations, deleteQuotation, "quotation");
  await tryEach(m.customerPrices, removeCustomerPrice, "customer price");
  await tryEach(m.priceListItems, removeItem, "price list item");
  await tryEach(m.priceLists, async (id) => { await archivePriceList(id); residue.push(`price list ${id}: archived (no hard delete)`); }, "price list");
  await tryEach(m.customerGroups, deleteCustomerGroup, "customer group");
  // Deletable products/customers delete cleanly (SET NULL onto cancelled docs); those on
  // a RETAINED issued credit note are pinned by its immutability lock and kept as history.
  await tryEach(m.products, deleteProduct, "product", "referenced by a retained invoice/credit note — kept as history");
  await tryEach(m.categories, deleteCategory, "category");
  await tryEach(m.customers, deleteCustomer, "customer", "referenced by a retained invoice/credit note — kept as history");
  await tryEach(m.expenses, deleteExpense, "expense");
  await tryEach(m.grns, deleteGRN, "goods received");
  await tryEach(m.purchaseOrders, deletePurchaseOrder, "purchase order");
  await tryEach(m.suppliers, deleteSupplier, "supplier");

  await setDemoManifest(null);   // clear the marker last
  return { cleared: true, residue };
}
